import { Modal } from "@/shared/ui/modal"
import { useAppDispatch, useAppSelector } from "@/shared/lib/redux"
import { SubmitHandler, useForm } from "react-hook-form"
import notify from "@/shared/lib/notify"
import { getErrorMessage } from "@/shared/consts/error-codes"
import { closeCancelBookingModal, selectBooking, selectIsCancelBookingModalOpen, selectOnCancel } from "../model"
import { cancelBooking } from "@/shared/api/aviabooking"
import { AxiosError } from "axios"
import { ErrorType } from "@/shared/types"
import { ButtonLoader } from "@/shared/ui/button-loader"
import { useEffect, useRef, useState } from "react"
import { formatMskDeadline } from "@/entities/booking/lib/cancel-availability"

interface CancelBookingFormValues {
    reason: string
}

export const CancelBookingModal = () => {
    const dispatch = useAppDispatch()
    const isOpen = useAppSelector(selectIsCancelBookingModalOpen)
    const onCancel = useAppSelector(selectOnCancel)
    const booking = useAppSelector(selectBooking)
    const requestInFlightRef = useRef(false)
    const [isRequestInFlight, setIsRequestInFlight] = useState(false)

    const handleClose = () => {
        if (requestInFlightRef.current) {
            return
        }

        dispatch(closeCancelBookingModal())
    }

    const onSubmit: SubmitHandler<CancelBookingFormValues> = async ({ reason }) => {
        if (requestInFlightRef.current) {
            return
        }

        requestInFlightRef.current = true
        setIsRequestInFlight(true)

        try {
            await cancelBooking(booking.booking_id, reason.trim())
            notify.success('Бронь успешно отменена')
            reset()
            onCancel?.()
            dispatch(closeCancelBookingModal())
        } catch(e) {
            const error = (e as AxiosError<ErrorType>)?.response?.data?.error_id
            notify.error(getErrorMessage(error))
        } finally {
            requestInFlightRef.current = false
            setIsRequestInFlight(false)
        }
    }

    const {
        handleSubmit,
        register,
        reset,
        formState: {
            isSubmitting
        }
    } = useForm<CancelBookingFormValues>({
        defaultValues: {
            reason: '',
        }
    })

    useEffect(() => {
        if (isOpen) {
            reset({ reason: '' })
        }
    }, [isOpen, reset])

    const formattedDeadline = booking.admin_cancel_deadline
        ? formatMskDeadline(booking.admin_cancel_deadline)
        : ''
    const isDeadlineExpired = booking.admin_cancel_deadline
        ? new Date(booking.admin_cancel_deadline).getTime() < Date.now()
        : false

    return (
        <Modal
            isOpen={isOpen}
            title="Отмена брони"
            onRequestClose={handleClose}
        >
            <form onSubmit={handleSubmit(onSubmit)} className="flex flex-col gap-3">
                <div>
                    Вы уверены, что хотите отменить выбранную бронь?
                </div>
                <div className="rounded-lg bg-orange-50 p-3 text-sm text-gray-700">
                    <div className="font-medium text-orange-800">Действие необратимо.</div>
                    <div>Полная сумма заказа вернётся на бонусный кошелёк сообщества.</div>
                    <div>Возврат выполняется не мгновенно.</div>
                    {formattedDeadline && (
                        <div>
                            {isDeadlineExpired
                                ? `Срок аннулирования истёк ${formattedDeadline}`
                                : `Аннулирование возможно до ${formattedDeadline}`
                            }
                        </div>
                    )}
                </div>
                <label className="flex flex-col gap-1 text-sm text-gray-700">
                    Причина отмены
                    <textarea
                        {...register('reason', { maxLength: 500 })}
                        maxLength={500}
                        rows={4}
                        placeholder="Необязательно"
                        className="w-full resize-none rounded-lg border border-gray-300 p-3 outline-none focus:border-color-primary"
                    />
                </label>
                <div className="w-full flex gap-3">
                    <button disabled={isRequestInFlight} onClick={handleClose} className="form_cancel_button" type="button">
                        Назад
                    </button>
                    <button disabled={isSubmitting || isRequestInFlight} className="form_handle_button inline-flex items-center justify-center gap-2" type="submit">
                        Подтвердить
                        {(isSubmitting || isRequestInFlight) && <ButtonLoader />}
                    </button>
                </div>
            </form>
        </Modal>
    )
}
